import { Color, Group, MathUtils } from 'three';
import { Scroll } from '../../_app/cuchillo/scroll/Scroll';
import { isMobile } from '../../_app/cuchillo/core/Basics';
import { UNDERWATER_SETTINGS } from '../SceneCompositor';
import { SEABED_SETTINGS } from './seabedSettings';
import SeabedTerrain from './SeabedTerrain';
import SeabedRocks from './SeabedRocks';

// FONDO MARINO del footer (Home / About). Objeto autocontenido: lo registra Scene (addSeabed) en su capa del
// 2º render (underwater) y Scene llama a loop/resize/dispose. Se ancla al `dom` (el footer): el fondo "sube"
// con el scroll y queda asentado cuando el footer termina de entrar.
//
// Es un terreno 3D REAL en perspectiva (cámara camera2D: 1 unidad ≈ 1px en z=0, cámara a z=1500 mirando en
// horizontal). El suelo está `floorY × alto` por debajo del ojo; la perspectiva hace sola el parallax (lo cercano
// se mueve más que lo lejano). Las rocas se colocan por su posición EN PANTALLA (sx, d, size) → layout robusto.
//
// Espacio local del grupo: y = 0 es la altura media del suelo.

const CAM_Z = 1500;
const SEED = 7;

export default class Seabed {
  constructor(dom) {
    this.dom = dom;
    this.group = new Group();                 // raíz (la añade Scene)
    this._pivot = new Group();                // gira alrededor de la cámara → inclina la vista hacia el suelo
    this._pivot.position.z = CAM_Z;
    this._world = new Group();                // terreno + rocas (se desplaza con el scroll)
    this._world.position.z = -CAM_Z;
    this._pivot.add(this._world);
    this.group.add(this._pivot);
    this._layerId = null;
    this._built = null;
    this._parts = [];
    this._bg = new Color();
    this._fog = new Color();
    this._clear = new Color();
    this._dark = new Color();
    this._haze = new Color();
    this._docBottom = 0;
    this.resize();
  }

  _build(W, H) {
    this._disposeParts();
    const s = SEABED_SETTINGS;
    // Calidad: desktop = ondulaciones de arena + relieve de piedra; móvil = modo ligero (menos ruido por píxel).
    const defines = { CAUSTIC_ITER: isMobile ? 3 : 4 };
    if (isMobile) defines.LITE = 1; else { defines.SAND_RIPPLE = 1; defines.ROCK_BUMP = 1; }

    this._u = {
      uCausticTime: { value: 0 },
      uCausticScale: { value: s.causticScale },
      uCausticStrength: { value: s.causticStrength },
      uLightContrast: { value: s.lightContrast },
      uLightScale: { value: s.lightScale },
      uCausticColor: { value: new Color(s.causticColor) },
      uFog: { value: this._fog },
      uFogBg: { value: this._bg },
      uFogDensity: { value: s.fogDensity },
      uFogPower: { value: s.fogPower },
      uFarDark: { value: s.farDark },
      uFarStart: { value: s.farStart },
      uFarEnd: { value: s.farEnd },
      uDim: { value: isMobile ? s.mobileDim : 1 },
    };
    this._clear.set(UNDERWATER_SETTINGS.clear);
    this._dark.set(UNDERWATER_SETTINGS.dark);
    this._haze.set(s.haze);

    const halfX = Math.max(W, 900) * 3.4;   // cubre el ancho visible incluso en el fondo lejano
    const terrain = new SeabedTerrain({ halfX, uniforms: this._u, defines, settings: s, seed: SEED, mobile: isMobile });

    const specs = isMobile ? s.rocks.filter((r) => r.mobile !== false).slice(0, s.mobileRocks) : s.rocks;
    const rocks = specs.map((r) => {
      const k = r.d / CAM_Z;                 // px de pantalla → unidades de mundo a esa distancia
      const x = r.sx * W * k, z = CAM_Z - r.d;
      const size = r.size * Math.min(H, W * 1.4) * 0.5 * k;   // radio horizontal (en vertical, referido al ancho)
      return { x, z, size, squash: r.squash, rot: r.rot, y: terrain.heightAt(x, z) + size * r.squash * 0.2 };
    });
    const rockMesh = new SeabedRocks({ rocks, heightAt: terrain.heightAt, uniforms: this._u, defines, settings: s, seed: SEED, mobile: isMobile });

    this._world.add(terrain.mesh, rockMesh.mesh);
    this._parts = [terrain, rockMesh];
    this._built = { W, H };
    if (this._layerId != null) this.setLayer(this._layerId);
    this.loop(0);
  }

  _disposeParts() {
    for (const p of this._parts) { this._world.remove(p.mesh); p.dispose(); }
    this._parts = [];
  }

  // Layer del double render (lo asigna Scene → SEABED_LAYER, 2º render / underwater).
  setLayer(n) {
    this._layerId = n;
    this.group.traverse((o) => o.layers.set(n));
  }

  // ── Frame (desde Scene.loop, sin lecturas de DOM) ─────────────────────────────
  loop(dt) {
    if (!this._built) return;
    const s = SEABED_SETTINGS;
    const H = window.innerHeight;
    this._u.uCausticTime.value += dt * s.causticSpeed;

    // t = px que le faltan al final del footer para estar asentado (0 con el scroll al final).
    const t = Math.max(0, this._docBottom - (Scroll.y || 0) - H);
    const y = -(s.floorY * H) - t * s.rise;
    this._pivot.rotation.x = MathUtils.degToRad(this._built.H > this._built.W ? s.pitchPortrait : s.pitch);
    this._world.position.y = y;
    this.group.visible = t * s.rise < 2.4 * H;   // muy lejos del footer el fondo queda fuera de pantalla → no se dibuja
    if (!this.group.visible) return;

    // Niebla = color de fondo underwater (gradiente por scroll global) teñido hacia el haze.
    this._bg.lerpColors(this._clear, this._dark, MathUtils.clamp(Scroll.progress ?? 0, 0, 1));
    this._fog.copy(this._bg).lerp(this._haze, s.hazeMix);
  }

  // Re-ancla al footer (única lectura de DOM) y reconstruye solo si el viewport cambió de verdad
  // (la barra de direcciones en móvil cambia H unos px → no se reconstruye).
  resize() {
    if (!this.dom) return;
    const r = this.dom.getBoundingClientRect();
    this._docBottom = r.bottom + (Scroll.y || 0);
    const W = window.innerWidth, H = window.innerHeight;
    if (!this._built || Math.abs(W - this._built.W) > 8 || Math.abs(H - this._built.H) > 150) this._build(W, H);
  }

  dispose() {
    this._disposeParts();
    this._built = null;
    this._u = null;
  }
}
